import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
// import { createFileLogger } from '../../../utils/logger';
// Reads for range detection and new node resolution should go through the facade
import { readAll as readAllDoc } from "../../services/doc/docApi";
import { generateNodeId } from '../../services/doc/docApi';
import { getDocContext as getDocCtx } from '../../services/doc/docApi';
import { replaceWithMarkdown as replaceMarkdown } from '../../services/doc/docApi';
import { traceLog } from '../../../../services/debug/traceHelpers';

// const log = createFileLogger('DOC_REPLACE_V2');

export const docReplaceV2Command: SimpleDocCommand = {
  id: 'doc_replace_v2',
  description: `🔄 REPLACE: Replace specific document nodes with new Markdown content.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- Use entity_write for Notes (note_*), Sheets (sheet_*), Canvas (canvas_*), Presentations (presentation_*)
`,
  logicalName: 'doc_replace',
  version: 'v2',
  appType: 'docs',
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document entity ID (llmId)',
        required: true,
      },
      {
        name: 'replaceNodes',
        type: 'array',
        description: 'Array of exact node IDs to replace with new content',
        required: true,
      },
      {
        name: 'markdownContent',
        type: 'string',
        description: 'New content in Markdown format',
        required: true,
      },
      {
        name: 'reasoning',
        type: 'string',
        description: 'Brief explanation of why this change is being made',
        required: true,
      },
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { id, replaceNodes, markdownContent, reasoning } = params;

    const traceId = context.traceId;
    traceLog({ traceId, type: 'tool_start', data: { tool: 'doc_replace_v2', input: { id, replaceNodesCount: Array.isArray(replaceNodes) ? replaceNodes.length : 'invalid' } } });
    // Emit initial loading state
    try { context.onProgress?.({ type: 'streaming_update', activity: 'Preparing replacement...' }); } catch {}

    try {
      // 🆕 NEW: ALWAYS clean up any streaming placeholder FIRST
      try {
        const { doc: preCleanupDoc, error: preCleanupError } = await getDocCtx(id, context);
        if (preCleanupDoc) {
          const yxmlArray = preCleanupDoc.yxml.toArray();
          const placeholders = yxmlArray.reduce((acc: number[], el: any, idx: number) => {
            if (el && typeof el.getAttribute === 'function' && el.nodeName) {
              const elemId = el.getAttribute('id');
              const isFailed = el.getAttribute('data-streaming-failed') === 'true';
              const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
              
              // Only delete if it's an active streaming placeholder or failed
              // Don't delete committed content that just happens to have a streaming_ ID
              if (isActiveStreaming || isFailed) {
                acc.push(idx);
              }
            }
            return acc;
          }, []).sort((a, b) => b - a);  // Delete from end first
          
          if (placeholders.length > 0) {
            console.log(`🧹 [DOC_REPLACE_PRE_CLEANUP] Found ${placeholders.length} streaming placeholder(s), removing`, {
              placeholders,
              toolUseId: context.executionId
            });
            
            preCleanupDoc.yDoc.transact(() => {
              placeholders.forEach(idx => {
                preCleanupDoc.yxml.delete(idx, 1);
              });
              console.log(`✅ [DOC_REPLACE_PRE_CLEANUP] ${placeholders.length} placeholder(s) deleted`);
            }, { source: 'tool-execution-cleanup', toolUseId: context.executionId });
          }
        }
      } catch (preCleanupError) {
        console.error(`❌ [DOC_REPLACE_PRE_CLEANUP_ERROR] Error cleaning placeholders:`, {
          error: preCleanupError instanceof Error ? preCleanupError.message : String(preCleanupError)
        });
        // Continue despite cleanup error
      }

      if (!Array.isArray(replaceNodes)) {
        const err = createErrorResult('Invalid replaceNodes', 'replaceNodes must be an array (can be empty or ["ALL"] to indicate full-document replacement)', 'validation');
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'validate_params', error: err } });
        return err;
      }
      
      const { doc, error } = await getDocCtx(id, context);
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Resolving document context...' }); } catch {}

      if (error || !doc) {
        const err = createErrorResult('Document not available', 'Y.js document not accessible', 'validation');
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'resolve_doc', error: err } });
        return err;
      }

      // Attach traceId to metadata for downstream tracing
      (doc as any).metadata = { ...(doc as any).metadata, traceId };

      // Guardrail: encourage iterative edits by limiting large spans
      const MAX_NODES_PER_CALL = 6;
      if (Array.isArray(replaceNodes) && replaceNodes.length > MAX_NODES_PER_CALL) {
        const err = createErrorResult(
          'Replace range too large',
          `Please target at most ${MAX_NODES_PER_CALL} contiguous nodes per call. Use multiple small passes.`,
          'validation'
        );
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'validate_span_size', error: err, requested: replaceNodes.length } });
        return err;
      }

      // Support full-document replacement when replaceNodes is empty or contains a sentinel
      let fromId: string;
      let toId: string;
      traceLog({ traceId, type: 'doc_replace_params', data: { replaceNodes, markdownPreview: String(markdownContent).slice(0, 80) } });
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Computing target span...' }); } catch {}

      if (replaceNodes.length === 0 || (replaceNodes.length === 1 && (String(replaceNodes[0]).toUpperCase() === '__ALL__' || String(replaceNodes[0]).toUpperCase() === 'ALL' || String(replaceNodes[0]).toLowerCase() === 'node_root'))) {
        const docContext = { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any;
        const all = readAllDoc(docContext);
        if (!all.nodes || all.nodes.length === 0) {
          // Empty document: insert the markdown as new content at start by clearing (no-op) and then replacing a synthetic empty range
          const placeholderId = generateNodeId();
          const respInit = replaceMarkdown(docContext as any, { from: placeholderId, to: placeholderId }, '', { batchDescription: 'init placeholder', restrictIds: [placeholderId] });
          if (!respInit || (respInit as any).ok === false) {
            const err = createErrorResult('Initialize failed', 'Failed to initialize empty document for replacement', 'execution');
            traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'init_empty_doc', error: err } });
            return err;
          }
          fromId = placeholderId;
          toId = placeholderId;
        } else {
          fromId = all.nodes[0].id;
          toId = all.nodes[all.nodes.length - 1].id;
        }
      } else {
        fromId = String(replaceNodes[0]);
        toId = String(replaceNodes[replaceNodes.length - 1]);
      }

      traceLog({ traceId, type: 'doc_replace_chosen_range', data: { fromId, toId, restrictIds: replaceNodes } });

      traceLog({ traceId, type: 'doc_replace_range', data: { fromId, toId, count: replaceNodes.length } });

      try { context.onProgress?.({ type: 'streaming_update', activity: 'Applying changes...' }); } catch {}
      
      // 🆕 NEW: Remove any streaming placeholder before replace
      try {
        const yxmlArray = doc.yxml.toArray();
        const placeholderIndex = yxmlArray.findIndex((el: any) => {
          if (el && typeof el.getAttribute === 'function' && el.nodeName) {
            const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
            const isFailed = el.getAttribute('data-streaming-failed') === 'true';
            return isActiveStreaming || isFailed;
          }
          return false;
        });
        
        if (placeholderIndex !== -1) {
          const placeholderEl = yxmlArray[placeholderIndex] as any;
          const placeholderId = placeholderEl && typeof placeholderEl.getAttribute === 'function' 
            ? placeholderEl.getAttribute('id') 
            : 'unknown';
          
          console.log(`🧹 [REPLACE_CLEANUP] Found streaming placeholder at index ${placeholderIndex}, removing`, {
            yxmlLength: yxmlArray.length,
            placeholderId
          });
          
          doc.yDoc.transact(() => {
            doc.yxml.delete(placeholderIndex, 1);
          }, { source: 'cleanup-streaming-placeholder-replace' });
          
          // Verify deletion
          const yxmlArrayAfter = doc.yxml.toArray();
          const stillExists = yxmlArrayAfter.some((el: any) => {
            if (el && typeof el.getAttribute === 'function' && el.nodeName) {
              const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
              const isFailed = el.getAttribute('data-streaming-failed') === 'true';
              return isActiveStreaming || isFailed;
            }
            return false;
          });
          
          if (stillExists) {
            console.error(`❌ [REPLACE_CLEANUP_ERROR] Placeholder still exists after deletion!`);
            throw new Error('Placeholder deletion verification failed');
          }
          
          console.log(`✅ [REPLACE_CLEANUP] Placeholder deleted successfully`, {
            yxmlLengthBefore: yxmlArray.length,
            yxmlLengthAfter: yxmlArrayAfter.length
          });
        } else {
          console.log(`ℹ️ [REPLACE_CLEANUP] No streaming placeholder found`, {
            yxmlLength: yxmlArray.length
          });
        }
      } catch (cleanupError) {
        console.error(`❌ [REPLACE_CLEANUP_ERROR] Error cleaning placeholder:`, {
          error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
          stack: cleanupError instanceof Error ? cleanupError.stack : undefined
        });
        throw cleanupError;  // ✅ FAIL if cleanup fails
      }
      
      const resp = replaceMarkdown({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any, { from: fromId, to: toId }, markdownContent, {
        batchDescription: reasoning,
        restrictIds: (Array.isArray(replaceNodes) && replaceNodes.length > 0) ? replaceNodes.map(String) : undefined,
      });

      if (!resp || (resp as any).ok === false) {
        const err = createErrorResult('Replace failed', 'Failed to replace nodes with Markdown content', 'execution');
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'execute', error: err } });
        return err;
      }

      const revisionId = (resp as any).revisionId;
      const insertedIds = (resp as any).insertedIds || [];
      const newNodes = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any).nodes.filter((n: any) => insertedIds.includes(n.id));
      const diffSummary = `Inserted ${newNodes.length} node(s); replaced ${replaceNodes.length}.`;

      const result = createSuccessResult(
        `Successfully replaced ${replaceNodes.length === 0 ? 'entire document' : `${replaceNodes.length} node(s)`} with new Markdown content.`,
        {
          type: 'document_replace',
          replacedCount: replaceNodes.length === 0 ? newNodes.length : replaceNodes.length,
          newNodes,
          revisionId,
          diffSummary,
        },
        {
          format: 'json',
          metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId] },
        }
      );
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Finalizing...', text_total: diffSummary }); } catch {}

      traceLog({ traceId, type: 'tool_end', data: { tool: 'doc_replace_v2', success: true, newNodes: newNodes.length, revisionId } });

      return result;

    } catch (error) {
      const err = createErrorResult('Replace operation failed', error instanceof Error ? error.message : 'Unknown error', 'execution');
      traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'catch', error: String(error) } });
      return err;
    }
  }
};
